import { doc, getDoc, getFirestore, collection, query, where, getDocs, writeBatch, deleteField } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/firebase';
import { BaseCRUDManager } from '../base/BaseCRUDManager';
import { TagManager } from '../TagManager';
import AlbumManager from '../AlbumManager';
import authenticationStore from '../../stores/AuthenticationStore';
import { auth } from '../../firebase';
import { ContentRating } from '../../types';
import { IRecording } from '../../types/Recording';
import { IRecordingWithModerationInfo, ModerationResult } from './types';
import { createRecordingWithModerationInfo } from './typeUtils';
import { recordingEventEmitter } from './events';
import { hasWordLevelTranscript } from '../../utils/recordingTranscription';
import { isExcludedFromTranscription } from '../../utils/transcriptionEligibility';
import type { TranscriptionExclusionReason } from '../../utils/transcriptionEligibility';
import { isDemoMode } from '../../utilities/demoMode';
import { DemoDataManager } from '../../services/demoDataManager';
import { ensureHasWordLevelTranscriptBackfill } from './hasWordLevelTranscriptBackfill';

const RECORDINGS_COLLECTION = 'Recordings';

function filterUntranscribedRecordings(recordings: IRecording[]): IRecording[] {
  return recordings
    .filter((recording) => !recording.isDemo)
    .filter((recording) => !hasWordLevelTranscript(recording))
    .filter((recording) => !isExcludedFromTranscription(recording))
    .sort((a, b) => {
      const aTime = a.createdAt?.toMillis?.() ?? 0;
      const bTime = b.createdAt?.toMillis?.() ?? 0;
      return bTime - aTime;
    });
}

/**
 * Fetch recordings that require moderation
 */
export async function fetchModerationRecordings(): Promise<IRecordingWithModerationInfo[]> {
  try {
    const db = getFirestore();
    const recordingsRef = collection(db, RECORDINGS_COLLECTION);
    const moderationQuery = query(recordingsRef, where('requiresModeration', '==', true));
    const snapshot = await getDocs(moderationQuery);

    const recordings: IRecordingWithModerationInfo[] = [];
    for (const docSnapshot of snapshot.docs) {
      const data = docSnapshot.data();
      
      // Skip recordings that already have a contentRating set
      if (data.contentRating) {
        console.log(`Skipping recording ${docSnapshot.id} - already has contentRating: ${data.contentRating}`);
        // Optionally, fix the data by setting requiresModeration to false
        try {
          await BaseCRUDManager.update<IRecording>(
            RECORDINGS_COLLECTION,
            docSnapshot.id,
            { requiresModeration: false } as Partial<Omit<IRecording, 'id' | 'createdAt'>>,
            { idField: 'key', autoTimestamps: false }
          );
          console.log(`Fixed recording ${docSnapshot.id} - set requiresModeration to false`);
        } catch (updateError) {
          console.warn(`Could not update recording ${docSnapshot.id}:`, updateError);
        }
        continue;
      }
      
      const recording = await createRecordingWithModerationInfo(data, docSnapshot.id);
      recordings.push(recording);
    }

    return recordings;
  } catch (error) {
    console.error('Error fetching moderation recordings:', error);
    throw error;
  }
}

/**
 * Fetch recordings that lack word-level transcription (no timing data).
 */
export async function fetchRecordingsWithoutTranscription(): Promise<IRecording[]> {
  try {
    if (isDemoMode()) {
      return filterUntranscribedRecordings(DemoDataManager.getAllRecordings());
    }

    await ensureHasWordLevelTranscriptBackfill();

    const recordingsRef = collection(db, RECORDINGS_COLLECTION);
    const untranscribedQuery = query(
      recordingsRef,
      where('hasWordLevelTranscript', '==', false)
    );
    const snapshot = await getDocs(untranscribedQuery);

    const recordings = snapshot.docs.map((docSnapshot) => ({
      key: docSnapshot.id,
      ...docSnapshot.data(),
    })) as IRecording[];

    return filterUntranscribedRecordings(recordings);
  } catch (error) {
    console.error('Error fetching recordings without transcription:', error);
    throw error;
  }
}

/**
 * Update content rating for a recording (recording doc + content-tag index in one batch).
 */
export async function updateContentRating(
  recordingKey: string,
  rating: ContentRating,
  currentModerationResult?: ModerationResult
): Promise<void> {
  try {
    if (!authenticationStore.isAuthenticated) {
      throw new Error('User must be authenticated to update content rating');
    }

    const recordingRef = doc(db, RECORDINGS_COLLECTION, recordingKey);
    const recordingDoc = await getDoc(recordingRef);
    const recordingData = recordingDoc.exists() ? recordingDoc.data() : null;
    const albumKey = recordingData?.albumKey;

    const existingModerationResult = recordingData?.moderationResult || {};
    const updatedModerationResult = {
      ...existingModerationResult,
      isAppropriate: true,
      flaggedCategories: existingModerationResult.flaggedCategories || [],
      flaggedPhrases: existingModerationResult.flaggedPhrases || [],
    };

    const batch = writeBatch(db);
    batch.update(recordingRef, {
      contentRating: rating,
      requiresModeration: false,
      moderationResult: updatedModerationResult,
      lastModifiedAt: new Date().toISOString(),
      moderatedAt: new Date().toISOString(),
    });
    await TagManager.appendContentRatingTagOps(batch, recordingKey, rating);
    await batch.commit();

    // Album aggregate depends on committed recording ratings
    if (albumKey) {
      await AlbumManager.updateAlbumContentRating(albumKey);
    }

    recordingEventEmitter.emit('recordingUpdated', recordingKey);
  } catch (error) {
    console.error('Error updating content rating:', error);
    throw error;
  }
}

/**
 * Remove content rating from a recording (recording doc + content-tag index in one batch).
 */
export async function removeContentRating(recordingKey: string): Promise<void> {
  try {
    const recordingRef = doc(db, RECORDINGS_COLLECTION, recordingKey);
    const recordingDoc = await getDoc(recordingRef);
    if (!recordingDoc.exists()) {
      return;
    }

    const data = recordingDoc.data();
    const currentRating = data.contentRating as ContentRating | undefined;
    if (!currentRating) {
      return;
    }

    const batch = writeBatch(db);
    batch.update(recordingRef, {
      contentRating: deleteField(),
      requiresModeration: true,
    });
    await TagManager.appendRemoveContentRatingTagOps(batch, recordingKey, currentRating);
    await batch.commit();

    recordingEventEmitter.emit('recordingUpdated', recordingKey);
  } catch (error) {
    console.error('Error removing content rating:', error);
    throw error;
  }
}

/**
 * Get recordings by content rating
 */
export async function getRecordingsByContentRating(
  rating: ContentRating
): Promise<IRecordingWithModerationInfo[]> {
  try {
    const recordingKeys = await TagManager.getRecordingsByContentRating(rating);
    const recordings: IRecordingWithModerationInfo[] = [];

    for (const key of recordingKeys) {
      const docSnapshot = await getDoc(doc(db, RECORDINGS_COLLECTION, key));
      if (docSnapshot.exists()) {
        const data = docSnapshot.data();
        const recording = await createRecordingWithModerationInfo(data, docSnapshot.id);
        recordings.push(recording);
      }
    }

    return recordings;
  } catch (error) {
    console.error('Error fetching recordings by content rating:', error);
    throw error;
  }
}

/**
 * Update approval status for a recording
 */
export async function updateApprovalStatus(
  recordingKey: string,
  status: 'pending' | 'approved' | 'rejected',
  approvedBy?: string
): Promise<void> {
  try {
    if (!authenticationStore.isAuthenticated) {
      throw new Error('User must be authenticated to update approval status');
    }

    const currentUser = auth.currentUser;
    const approverId = approvedBy || currentUser?.uid || '';

    const updateData: any = {
      approvalStatus: status,
      approvedAt: Timestamp.now(),
      approvedBy: approverId,
    };

    // If status is pending, set submittedAt if not already set
    if (status === 'pending') {
      const recordingDoc = await getDoc(doc(db, RECORDINGS_COLLECTION, recordingKey));
      const recordingData = recordingDoc.exists() ? recordingDoc.data() : null;
      if (!recordingData?.submittedAt) {
        updateData.submittedAt = Timestamp.now();
      }
    }

    await BaseCRUDManager.update(
      RECORDINGS_COLLECTION,
      recordingKey,
      updateData,
      { idField: 'key', autoTimestamps: false }
    );

    recordingEventEmitter.emit('recordingUpdated', recordingKey);
  } catch (error) {
    console.error('Error updating approval status:', error);
    throw error;
  }
}

/**
 * Exclude a recording from the transcription queue (e.g. non-English audio).
 */
export async function markRecordingTranscriptionExcluded(
  recordingKey: string,
  reason: TranscriptionExclusionReason
): Promise<void> {
  try {
    if (!authenticationStore.isAuthenticated) {
      throw new Error('User must be authenticated to update transcription exclusion');
    }

    await BaseCRUDManager.update<IRecording>(
      RECORDINGS_COLLECTION,
      recordingKey,
      {
        transcriptionExcludedReason: reason,
        lastModifiedAt: new Date().toISOString(),
      } as Partial<Omit<IRecording, 'id' | 'createdAt'>>,
      { idField: 'key', autoTimestamps: false }
    );

    recordingEventEmitter.emit('recordingUpdated', recordingKey);
  } catch (error) {
    console.error('Error marking recording as transcription excluded:', error);
    throw error;
  }
}
